import { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const CATEGORIES  = ['Sequence', 'Capability', 'Safety', 'Timing', 'Part', 'Fault'];
const SEVERITIES  = ['Critical', 'Major', 'Minor'];
const CHECK_TYPES = ['event_order', 'threshold_min', 'threshold_max', 'duration_max', 'event_absent', 'state_forbidden'];

const BLANK_RULE = {
  id: '', category: 'Sequence', severity: 'Critical',
  description: '', check: 'event_order',
  min_value: '', max_value: '', max_duration_s: '',
  forbidden_state: '', event_pattern: '',
};

export default function RulesEditor() {
  const [rules,       setRules]       = useState([]);
  const [version,     setVersion]     = useState('—');
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');
  const [showForm,    setShowForm]    = useState(false);
  const [editIndex,   setEditIndex]   = useState(null);
  const [form,        setForm]        = useState(BLANK_RULE);

  useEffect(() => { loadRules(); }, []);

  const loadRules = async () => {
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API}/api/rules`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load rules');
      setRules(data.rules || []);
      setVersion(data.version || '—');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setForm(BLANK_RULE);
    setEditIndex(null);
    setShowForm(true);
    setSuccess('');
  };

  const openEdit = (idx) => {
    setForm({ ...BLANK_RULE, ...rules[idx] });
    setEditIndex(idx);
    setShowForm(true);
    setSuccess('');
  };

  const cancelForm = () => { setShowForm(false); setEditIndex(null); };

  const saveForm = () => {
    if (!form.id.trim()) { setError('Rule ID is required.'); return; }
    if (!form.description.trim()) { setError('Description is required.'); return; }
    setError('');
    const updated = [...rules];
    if (editIndex !== null) {
      updated[editIndex] = { ...form };
    } else {
      if (updated.find(r => r.id === form.id.trim())) {
        setError(`Rule ID "${form.id}" already exists.`);
        return;
      }
      updated.push({ ...form });
    }
    setRules(updated);
    setShowForm(false);
    setEditIndex(null);
  };

  const toggleActive = (idx) => {
    const updated = [...rules];
    updated[idx] = { ...updated[idx], active: !(updated[idx].active !== false) };
    setRules(updated);
  };

  const saveAndBump = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res  = await fetch(`${API}/api/rules`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ rules }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Save failed');
      setVersion(data.newVersion);
      setSuccess(`✅ Saved as v${data.newVersion} (was v${data.previousVersion}). ${data.ruleCount} rules active.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h2 style={s.heading}>📐 Rules Editor</h2>
          <p style={s.sub}>View, add, and edit validation rules. Saving bumps the rule set version.</p>
        </div>
        <div style={s.versionBadge}>
          Active: <strong>v{version}</strong>
        </div>
      </div>

      {error   && <div style={s.error}>{error}</div>}
      {success && <div style={s.successBox}>{success}</div>}

      {/* ── Toolbar ── */}
      <div style={s.toolbar}>
        <button style={s.addBtn} onClick={openAdd}>+ Add Rule</button>
        <button style={s.saveBtn} onClick={saveAndBump} disabled={saving || loading}>
          {saving ? 'Saving…' : '💾 Save & Bump Version'}
        </button>
        <button style={s.refreshBtn} onClick={loadRules} disabled={loading}>↻ Reload</button>
      </div>

      {/* ── Inline form ── */}
      {showForm && (
        <div style={s.formCard}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>{editIndex !== null ? 'Edit Rule' : 'Add New Rule'}</h3>
          <div style={s.formGrid}>
            <Field label="Rule ID *" value={form.id} onChange={v => setForm(f => ({ ...f, id: v }))} placeholder="R_WELD_001" />
            <Select label="Category" value={form.category} options={CATEGORIES} onChange={v => setForm(f => ({ ...f, category: v }))} />
            <Select label="Severity" value={form.severity} options={SEVERITIES} onChange={v => setForm(f => ({ ...f, severity: v }))} />
            <Select label="Check Type" value={form.check} options={CHECK_TYPES} onChange={v => setForm(f => ({ ...f, check: v }))} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={s.label}>Description *</label>
            <input style={{ ...s.input, width: '100%' }} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Human-readable rule description" />
          </div>
          {/* Conditional threshold fields */}
          {form.check === 'threshold_min' && (
            <Field label="Min Value (N)" value={form.min_value} onChange={v => setForm(f => ({ ...f, min_value: v }))} placeholder="1200" />
          )}
          {form.check === 'threshold_max' && (
            <Field label="Max Value" value={form.max_value} onChange={v => setForm(f => ({ ...f, max_value: v }))} placeholder="5000" />
          )}
          {form.check === 'duration_max' && (
            <Field label="Max Duration (s)" value={form.max_duration_s} onChange={v => setForm(f => ({ ...f, max_duration_s: v }))} placeholder="3.0" />
          )}
          {form.check === 'state_forbidden' && (
            <Field label="Forbidden State" value={form.forbidden_state} onChange={v => setForm(f => ({ ...f, forbidden_state: v }))} placeholder="CRITICAL" />
          )}
          {form.check === 'event_absent' && (
            <Field label="Event Pattern" value={form.event_pattern} onChange={v => setForm(f => ({ ...f, event_pattern: v }))} placeholder="COLLISION:" />
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button style={s.addBtn} onClick={saveForm}>{editIndex !== null ? 'Update Rule' : 'Add Rule'}</button>
            <button style={s.cancelBtn} onClick={cancelForm}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── Rules table ── */}
      {loading ? (
        <p style={{ color: '#888', textAlign: 'center', padding: 40 }}>Loading rules…</p>
      ) : (
        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr style={s.thead}>
                <th style={s.th}>Rule ID</th>
                <th style={s.th}>Category</th>
                <th style={s.th}>Severity</th>
                <th style={s.th}>Description</th>
                <th style={s.th}>Check</th>
                <th style={s.th}>Threshold</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule, idx) => {
                const active = rule.active !== false;
                return (
                  <tr key={rule.id} style={{ background: active ? '#fff' : '#f9f9f9', opacity: active ? 1 : 0.55 }}>
                    <td style={{ ...s.td, fontWeight: 600, fontFamily: 'monospace', color: '#0073bb' }}>{rule.id}</td>
                    <td style={s.td}><span style={{ ...s.catBadge, background: _catColor(rule.category) }}>{rule.category}</span></td>
                    <td style={s.td}><span style={{ ...s.sevBadge, color: _sevColor(rule.severity) }}>{rule.severity}</span></td>
                    <td style={{ ...s.td, maxWidth: 260, fontSize: 12 }}>{rule.description}</td>
                    <td style={{ ...s.td, fontSize: 12, fontFamily: 'monospace' }}>{rule.check}</td>
                    <td style={{ ...s.td, fontSize: 12 }}>{_thresholdLabel(rule) || '—'}</td>
                    <td style={s.td}>
                      <span style={{ color: active ? '#27ae60' : '#aaa', fontWeight: 600, fontSize: 12 }}>
                        {active ? '● Active' : '○ Disabled'}
                      </span>
                    </td>
                    <td style={s.td}>
                      <button style={s.editBtn} onClick={() => openEdit(idx)}>Edit</button>
                      <button style={s.toggleBtn} onClick={() => toggleActive(idx)}>
                        {active ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {rules.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#aaa' }}>No rules loaded.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#444' }}>{label}</label>
      <input style={{ padding: '7px 10px', border: '1px solid #ccc', borderRadius: 5, fontSize: 13 }}
        value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function Select({ label, value, options, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#444' }}>{label}</label>
      <select style={{ padding: '7px 10px', border: '1px solid #ccc', borderRadius: 5, fontSize: 13 }}
        value={value} onChange={e => onChange(e.target.value)}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function _thresholdLabel(rule) {
  if (rule.check === 'threshold_min')   return `≥ ${rule.min_value} N`;
  if (rule.check === 'threshold_max')   return `≤ ${rule.max_value}`;
  if (rule.check === 'duration_max')    return `≤ ${rule.max_duration_s} s`;
  if (rule.check === 'state_forbidden') return `no ${rule.forbidden_state}`;
  if (rule.check === 'event_absent')    return `absent: ${rule.event_pattern || ''}`;
  return null;
}

function _catColor(cat) {
  const m = { Sequence: '#d5e8d4', Capability: '#dae8fc', Safety: '#f8cecc', Timing: '#fff2cc', Part: '#e1d5e7', Fault: '#ffe6cc' };
  return m[cat] || '#eee';
}

function _sevColor(sev) {
  return sev === 'Critical' ? '#c0392b' : sev === 'Major' ? '#e67e22' : '#27ae60';
}

const s = {
  page:       { maxWidth: 1100, margin: '0 auto', fontFamily: 'sans-serif' },
  header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 },
  heading:    { fontSize: 22, fontWeight: 700, marginBottom: 4 },
  sub:        { color: '#666', fontSize: 14, margin: 0 },
  versionBadge: { background: '#0073bb', color: '#fff', padding: '8px 16px', borderRadius: 20, fontSize: 14 },
  error:      { background: '#fdecea', color: '#c0392b', padding: '10px 16px', borderRadius: 6, marginBottom: 12, fontSize: 13 },
  successBox: { background: '#eafaf1', color: '#1e8449', padding: '10px 16px', borderRadius: 6, marginBottom: 12, fontSize: 13 },
  toolbar:    { display: 'flex', gap: 10, marginBottom: 16 },
  addBtn:     { padding: '8px 18px', background: '#27ae60', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  saveBtn:    { padding: '8px 18px', background: '#0073bb', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  refreshBtn: { padding: '8px 14px', background: '#f0f4f8', color: '#333', border: '1px solid #ccc', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  cancelBtn:  { padding: '8px 18px', background: '#eee', color: '#333', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  formCard:   { background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: 20, marginBottom: 20 },
  formGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 12 },
  label:      { fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 4 },
  input:      { padding: '7px 10px', border: '1px solid #ccc', borderRadius: 5, fontSize: 13 },
  card:       { background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.1)', overflow: 'auto' },
  table:      { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  thead:      { background: '#f0f4f8' },
  th:         { padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#333', borderBottom: '2px solid #ddd', whiteSpace: 'nowrap' },
  td:         { padding: '10px 12px', borderBottom: '1px solid #eee', verticalAlign: 'middle' },
  catBadge:   { padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 },
  sevBadge:   { fontWeight: 600, fontSize: 12 },
  editBtn:    { padding: '4px 10px', background: '#eaf4fb', color: '#1a5276', border: '1px solid #aed6f1', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 },
  toggleBtn:  { padding: '4px 10px', background: '#f9f9f9', color: '#555', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', fontSize: 12 },
};
